/**
 * MorphIcon — анимированная иконка voice ↔ text
 *
 * 3 SVG rect элемента с SMIL анимацией:
 * - Voice: 3 вертикальных бара пульсируют по высоте (эквалайзер)
 * - Переход через точки: бары сжимаются в круги (w=4, h=4, rx=2)
 * - Text: точки перемещаются и раздвигаются в горизонтальные линии
 * - Каскадный эффект: элементы появляются последовательно (1→2→3)
 */

import { memo } from 'react';
import PropTypes from 'prop-types';
import './MorphIcon.css';

const DUR = '5s';
const SP = '0.25 0.1 0.25 1';

// 11 keyTimes: bar → pulse → dot → move → line(draw) → hold → line(erase) → move → bar
//              bar   p↑    ↓    →dot  →pos  →draw  hold  →erase →pos   bar
const K1 = '0;0.12;0.24;0.30;0.36;0.42;0.56;0.64;0.72;0.80;1';
const K2 = '0;0.12;0.24;0.30;0.36;0.46;0.56;0.64;0.70;0.83;1';
const K3 = '0;0.12;0.24;0.30;0.36;0.50;0.56;0.64;0.68;0.86;1';
const SPLINES = Array(10).fill(SP).join(';');

export const MorphIcon = memo(({
  size = 40,
  iconSize = 16,
  color,
}) => {
  const c = color || 'var(--widget-text, #0E0F10)';

  return (
    <div className="morph-icon" style={{ width: `${size}px`, height: `${size}px` }}>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize}>

        {/* Bar 1 (x=2): bar → pulse → dot → text line 1 → dot → bar */}
        <rect x="2" y="5.8" width="4" height="12.4" rx="2" fill={c}>
          <animate attributeName="x"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K1} values="2; 2; 2; 2; 0; 0; 0; 0; 0; 2; 2" />
          <animate attributeName="y"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K1} values="5.8; 1; 5.8; 11; 2; 2; 2; 2; 2; 11; 5.8" />
          <animate attributeName="height" dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K1} values="12.4; 22; 12.4; 4; 4; 3.3; 3.3; 3.3; 4; 4; 12.4" />
          <animate attributeName="width"  dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K1} values="4; 4; 4; 4; 4; 24; 24; 24; 4; 4; 4" />
        </rect>

        {/* Bar 2 (x=10): bar → pulse → dot → text line 2 → dot → bar */}
        <rect x="10" y="0" width="4" height="24" rx="2" fill={c}>
          <animate attributeName="x"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K2} values="10; 10; 10; 10; 0; 0; 0; 0; 0; 10; 10" />
          <animate attributeName="y"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K2} values="0; 8; 0; 11; 10; 10; 10; 10; 10; 11; 0" />
          <animate attributeName="height" dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K2} values="24; 8; 24; 4; 4; 3.3; 3.3; 3.3; 4; 4; 24" />
          <animate attributeName="width"  dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K2} values="4; 4; 4; 4; 4; 24; 24; 24; 4; 4; 4" />
        </rect>

        {/* Bar 3 (x=18): bar → pulse → dot → text line 3 → dot → bar */}
        <rect x="18" y="5.3" width="4" height="13.5" rx="2" fill={c}>
          <animate attributeName="x"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K3} values="18; 18; 18; 18; 0; 0; 0; 0; 0; 18; 18" />
          <animate attributeName="y"      dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K3} values="5.3; 0; 5.3; 11; 18; 18; 18; 18; 18; 11; 5.3" />
          <animate attributeName="height" dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K3} values="13.5; 24; 13.5; 4; 4; 3.3; 3.3; 3.3; 4; 4; 13.5" />
          <animate attributeName="width"  dur={DUR} repeatCount="indefinite" calcMode="spline" keySplines={SPLINES}
            keyTimes={K3} values="4; 4; 4; 4; 4; 17; 17; 17; 4; 4; 4" />
        </rect>

      </svg>
    </div>
  );
});

MorphIcon.displayName = 'MorphIcon';

MorphIcon.propTypes = {
  size: PropTypes.number,
  iconSize: PropTypes.number,
  color: PropTypes.string,
};
